import React, { useRef, useState } from "react";
import { Pause, Play, RotateCcw, RotateCw } from "lucide-react";
import { trackPodcastAction } from "./podcastAnalytics";

const TICK_COUNT = 72;
function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
}

export default function CircularPodcastPlayer({ src, builderId, runId, children }) {
  const audioRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playbackError, setPlaybackError] = useState(false);
  const progress = duration > 0 ? Math.min(currentTime / duration, 1) : 0;

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!audio.paused) {
      audio.pause();
      return;
    }
    try {
      await audio.play();
      setPlaybackError(false);
    } catch {
      setPlaybackError(true);
    }
  };

  const skip = (seconds) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(Number.isFinite(audio.duration) ? audio.duration : Infinity, audio.currentTime + seconds));
  };

  const seekOnDial = (event) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - bounds.left - bounds.width / 2;
    const y = event.clientY - bounds.top - bounds.height / 2;
    const fraction = (Math.atan2(x, -y) + Math.PI * 2) % (Math.PI * 2) / (Math.PI * 2);
    audio.currentTime = fraction * audio.duration;
  };

  const seekWithKeys = (event) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    if (event.key === "ArrowRight" || event.key === "ArrowUp") skip(5);
    else if (event.key === "ArrowLeft" || event.key === "ArrowDown") skip(-5);
    else if (event.key === "Home") audio.currentTime = 0;
    else if (event.key === "End") audio.currentTime = audio.duration;
    else return;
    event.preventDefault();
  };

  return <div className="podcast-listen-player">
    <audio
      ref={audioRef}
      src={src}
      preload="metadata"
      onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
      onDurationChange={(event) => setDuration(event.currentTarget.duration)}
      onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
      onPlay={() => {
        setPlaying(true);
        trackPodcastAction("play", { builderId, runId, source: "listen" });
      }}
      onPause={() => setPlaying(false)}
      onEnded={() => setPlaying(false)}
      onError={() => setPlaybackError(true)}
    />
    <div className="podcast-listen-dial">
      <svg className="podcast-listen-ticks" viewBox="0 0 300 300" role="slider" tabIndex={0}
        aria-label="Podcast position" aria-valuemin={0} aria-valuemax={Number.isFinite(duration) ? Math.floor(duration) : 0}
        aria-valuenow={Math.floor(currentTime)} aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
        onClick={seekOnDial} onKeyDown={seekWithKeys}>
        <g className="podcast-listen-ticks-base">
          {Array.from({ length: TICK_COUNT }, (_, index) =>
            <line className={index <= Math.floor(progress * TICK_COUNT) ? "is-active" : ""} key={index} x1="150" y1="12" x2="150" y2="29" transform={`rotate(${(index / TICK_COUNT) * 360} 150 150)`} />)}
        </g>
        <circle className="podcast-listen-tick-marker" cx="150" cy="20" r="4" transform={`rotate(${progress * 360} 150 150)`} />
        <circle className="podcast-listen-seek-target" cx="150" cy="150" r="130" />
      </svg>
      <button className="podcast-listen-play" type="button" onClick={togglePlayback} aria-label={playing ? "Pause podcast" : "Play podcast"}>
        {playing ? <Pause size={34} fill="currentColor" /> : <Play size={36} fill="currentColor" />}
      </button>
    </div>
    {children}
    <div className="podcast-listen-controls">
      <button type="button" onClick={() => skip(-10)} aria-label="Back 10 seconds"><RotateCcw size={25} /><span>10</span></button>
      <span className="podcast-listen-time" aria-label={`${formatTime(currentTime)} of ${formatTime(duration)}`}>{formatTime(currentTime)} <span>/</span> {formatTime(duration)}</span>
      <button type="button" onClick={() => skip(10)} aria-label="Forward 10 seconds"><RotateCw size={25} /><span>10</span></button>
    </div>
    {playbackError && <p className="podcast-listen-playback-error" role="alert">Audio could not be played. Please try again.</p>}
  </div>;
}
